/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import { useCallback } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';
import type { MutationUpdaterFn } from 'apollo-client';

import { getLogger } from '@confluence/logger';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { confluenceSessionStorageInstance as sessionStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import type {
	useFeatureDiscoveryMarkFeatureDiscoveredMutation as useFeatureDiscoveryMarkFeatureDiscoveredMutation$data,
	useFeatureDiscoveryMarkFeatureDiscoveredMutationVariables as useFeatureDiscoveryMarkFeatureDiscoveredMutation$variables,
} from './__types__/useFeatureDiscoveryMarkFeatureDiscoveredMutation.ts';
import type { useFeatureDiscoveryFeatureDiscoveryQuery as useFeatureDiscoveryFeatureDiscoveryQuery$data } from './__types__/useFeatureDiscoveryFeatureDiscoveryQuery.ts';
import { FeatureDiscoveryQuery } from './FeatureDiscoveryQuery.graphql.ts';
import type {
	FeatureDiscoveryQuery as FeatureDiscoveryQueryType,
	FeatureDiscoveryQuery_featureDiscovery as FeatureDiscovery,
} from './__types__/FeatureDiscoveryQuery.ts';
import type { MarkFeatureDiscoveredMutation as MarkFeatureDiscoveredMutationType } from './__types__/MarkFeatureDiscoveredMutation.ts';

const useFeatureDiscoveryFeatureDiscoveryQuery = graphql`
	query useFeatureDiscoveryFeatureDiscoveryQuery {
		featureDiscovery {
			pluginKey
			featureKey
		}
	}
`;

const useFeatureDiscoveryMarkFeatureDiscoveredMutation = graphql`
	mutation useFeatureDiscoveryMarkFeatureDiscoveredMutation(
		$featureKey: String!
		$pluginKey: String!
	) {
		markFeatureDiscovered(featureKey: $featureKey, pluginKey: $pluginKey) {
			pluginKey
			featureKey
		}
	}
`;

const logger = getLogger('useFeatureDiscovery');
export const PLUGIN_KEY = 'com.atlassian.confluence.plugins.confluence-frontend-support';

const updateCache: MutationUpdaterFn<MarkFeatureDiscoveredMutationType> = (cache, { data }) => {
	const newMarkedFeature = data?.markFeatureDiscovered;
	const cachedFeatureData = cache.readQuery<FeatureDiscoveryQueryType>({
		query: FeatureDiscoveryQuery,
	});
	if (newMarkedFeature && cachedFeatureData?.featureDiscovery) {
		cache.writeQuery<FeatureDiscoveryQueryType>({
			query: FeatureDiscoveryQuery,
			data: {
				featureDiscovery: cachedFeatureData.featureDiscovery.concat(newMarkedFeature),
			},
		});
	}
};

const getLocalOverrideValue = (featureKey: string): boolean => {
	try {
		if (sessionStorage.getItem(`${keys.FEATURE_DISCOVERY_OVERRIDE_PREFIX}${featureKey}`)) {
			return true;
		}
	} catch (e) {
		logger.error(e);
	}
	return false;
};

const noop = () => {};

/**
 * @deprecated Please use `useOnboardingState` instead.
 */
export type UseFeatureDiscoveryResponse = [boolean, () => void, Error | undefined];

/**
 * @deprecated Please use `useOnboardingState` instead.
 */
export const useFeatureDiscovery = (
	featureKey: string,
	skip?: boolean,
): UseFeatureDiscoveryResponse => {
	const { isLoggedIn } = useSessionData();
	const skipFeatureDiscoveryQuery = !isLoggedIn || skip;

	const {
		data,
		loading: queryLoading,
		error: queryError,
	} = useQuery<useFeatureDiscoveryFeatureDiscoveryQuery$data>(
		useFeatureDiscoveryFeatureDiscoveryQuery,
		{
			skip: skipFeatureDiscoveryQuery,
		},
	);
	const [markFeatureDiscovered, { loading: mutationLoading, error: mutationError }] = useMutation<
		useFeatureDiscoveryMarkFeatureDiscoveredMutation$data,
		useFeatureDiscoveryMarkFeatureDiscoveredMutation$variables
	>(useFeatureDiscoveryMarkFeatureDiscoveredMutation, {
		variables: {
			featureKey,
			pluginKey: PLUGIN_KEY,
		},
		update: updateCache,
	});
	const loading = queryLoading || mutationLoading;
	const error = queryError || mutationError;

	const showFeature =
		!loading &&
		!error &&
		!data?.featureDiscovery?.some(
			(feature: FeatureDiscovery | null) =>
				feature?.pluginKey === PLUGIN_KEY && feature?.featureKey === featureKey,
		);

	const handleStop = useCallback(() => {
		if (showFeature) {
			// error is handled in the useMutation result
			markFeatureDiscovered().catch(() => {});
		}
	}, [showFeature, markFeatureDiscovered]);

	if (skipFeatureDiscoveryQuery) {
		return [false, noop, undefined];
	}

	// this is for dev/testing, since we cant easily remove data from the featureDiscovery array
	const forceShowFeature = showFeature || getLocalOverrideValue(featureKey);

	return [forceShowFeature, handleStop, error];
};
